import { forEach, get, isEmpty as empty, set } from 'lodash';
import { connect } from 'react-redux';

import Cart from 'components/Cart';
import * as actions from 'state/actions';
import ACTIONS, { CART_VIEWS } from 'state/constants';
import CartSelector from 'state/selectors/cart';
import Device from 'utils/Device';
import Environment from 'utils/Environment';
import { DIGITAL_WALLETS } from 'utils/Money/constants';
import Rudderstack from 'utils/Rudderstack';
import GENERIC_CTA_CLICKED_EVENT from 'utils/Rudderstack/constants';
import Settings from 'utils/Settings';
import { stringifyQueryString, getLocationOrigin } from 'utils/Url';

// Get list of ids of items that are being added to the cart and aren't already in the cart
export const getNewItemsBeingAdded = (state, itemIds) => {
  const itemsBeingAdded = get(state, ['ajaxCart', 'itemsBeingAdded']);
  const newItemsBeingAdded = [];
  const itemIdMap = {};

  // Make map of item ids that are already in the cart
  forEach(itemIds, id => {
    set(itemIdMap, id, true);
  });

  // Loop over items being added to the cart
  forEach(itemsBeingAdded, (quantity, id) => {
    if (!get(itemIdMap, id)) {
      // If item not already in cart, add to list
      newItemsBeingAdded.push(id);
    }
  });

  return newItemsBeingAdded;
};

// Logic to show apple pay button depending on the device
const showApplePay = () => {
  let applePayEnabled = false;

  if (Device.isAppleDevice && (Device.isMobile || Device.isTable)) {
    applePayEnabled = true;
  } else if (Device.isAppleDevice && Device.isDesktop && Environment.isSafariDesktop) {
    applePayEnabled = true;
  }

  return applePayEnabled;
};

const mapStateToProps = state => {
  const cartView = get(state, ['ajaxCart', 'view']);
  const {
    giftMessagingEnabled,
    freeShippingEnabled,
    logoFreePackagingEnabled,
    logoFreeProductsBlocklist,
    priceChangeMessagingEnabled,
  } = Settings;

  const {
    subtotalWithCurrency,
    hasFreeShipping,
    giftMessage,
    itemCount,
    itemIds,
    useLogoFreePackaging,
  } = CartSelector.getCart(state);

  const isBeingInitialized = get(state, ['ajaxCart', 'isBeingInitialized']);
  const errorMessage = get(state, ['ajaxCart', 'errorMessage'], null);
  const message = get(state, ['ajaxCart', 'message'], null);
  const apiData = get(state, ['ajaxCart', 'data']);
  const isLoading = apiData === null || isBeingInitialized;
  const newItemsBeingAdded = getNewItemsBeingAdded(state, itemIds);
  const isEmpty = empty(newItemsBeingAdded) && !itemCount;

  const isElligibleForGiftMessage = CartSelector.getIsElligibleForGiftMessage(state);
  const isElligibleForLogoFree = CartSelector.getIsElligibleForLogoFree(
    state,
    logoFreeProductsBlocklist
  );

  const showUpsell = Settings.cartUpsellsEnabled;
  const isOpen = CartSelector.getIsCartOpen(state);

  const ajaxCartUrl = `/checkout?${stringifyQueryString({
    locale: Settings.currentLocale,
  })}`;

  let checkoutUrl = ajaxCartUrl;

  if (Settings.storefrontCartEnabled) {
    const storefrontCartUrl = CartSelector.getStorefrontCartCheckoutUrl(state);

    if (storefrontCartUrl) {
      const url = new URL(storefrontCartUrl);

      url.searchParams.set('locale', Settings.currentLocale);

      // Override checkout URL domain with current domain, else use the one provide by Cart API
      if (Settings.cartOverrideCheckoutUrlDomain) {
        checkoutUrl = `${getLocationOrigin()}${url.pathname}${url.search}`;
      } else {
        checkoutUrl = url.toString();
      }
    } else {
      checkoutUrl = null;
    }
  }

  const showShoprunner = Settings.shoprunnerEnabled;

  // Get supported digital wallets form state
  const supportedDigitalWallets = get(
    state,
    ['shop', 'paymentSettings', 'supportedDigitalWallets'],
    []
  );

  // Enable digital wallet per device
  const digitalWalletsPerDevice = supportedDigitalWallets.filter(digitalWAlletKey => {
    const digitalWAlletToRemove = showApplePay()
      ? DIGITAL_WALLETS.AMAZON_PAY
      : DIGITAL_WALLETS.APPLE_PAY;
    return digitalWAlletKey != digitalWAlletToRemove;
  });

  const mappedProps = {
    giftMessage,
    itemCount,
    itemIds,
    subtotalWithCurrency,
    hasFreeShipping,
    hasFreeShippingIndicator: freeShippingEnabled,
    isGiftMessagingOn: giftMessagingEnabled && isElligibleForGiftMessage,
    isLogoFreePackagingAvailable: logoFreePackagingEnabled && isElligibleForLogoFree,
    isOpen,
    isLoading,
    isBeingCleared: get(state, ['ajaxCart', 'isBeingCleared']),
    isSettingGiftingOptions: get(state, ['ajaxCart', 'isSettingGiftingOptions']),
    showUpsell,
    isViewingOverview: cartView === CART_VIEWS.OVERVIEW,
    isViewingGiftMessage: cartView === CART_VIEWS.GIFT_MESSAGE,
    newItemsBeingAdded,
    isEmpty,
    errorMessage,
    message,
    useLogoFreePackaging,
    checkoutUrl,
    isPriceChangeMessagingEnabled: priceChangeMessagingEnabled,
    showShoprunner,
    acceleratedPaymentMethodsData: {
      supportedDigitalWallets: digitalWalletsPerDevice,
    },
  };

  return mappedProps;
};

const mapDispatchToProps = dispatch => ({
  updateLineItem: (id, quantity) => dispatch(actions.updateLineItem(id, quantity)),
  removeLineItem: id => dispatch(actions.removeLineItem(id)),
  clear: () => dispatch(actions.clearCart()),
  open: () => dispatch(actions.openCart()),
  close: () => dispatch(actions.closeCart()),
  viewGiftMessage: () => {
    dispatch(actions.setCartViewToGiftMessage());
    Rudderstack.giftMessageClicked();
  },
  viewOverview: () => dispatch(actions.setCartViewToOverview()),
  setGiftingOptions: ({ message, logoFreePackaging }) =>
    dispatch(actions.setGiftingOptions({ message, logoFreePackaging })),
  trackAcceleratedPaymentMethodClick: ({ alt, name, url }) => {
    dispatch(
      actions.trackEngagementClick({
        eventCategory: 'Cart',
        eventAction: 'AcceleratedPaymentMethodClick',
        eventLabel: `AcceleratedPaymentMethod - ${alt}`,
      })
    );

    dispatch({
      type: ACTIONS.TRACK_CTA_CLICK_EVENT,
      payload: {
        type: GENERIC_CTA_CLICKED_EVENT.TYPES.BUTTON,
        location: GENERIC_CTA_CLICKED_EVENT.LOCATIONS.CART,
        name,
        url,
      },
    });

    // Because this function will be attached to an <a> tag's onClick handler, it needs to return true to not prevent navigation
    return true;
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Cart);
